// -- [Sticky role/topic bar; mobile About-you dialog; lg inline nav]

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import * as Select from '@radix-ui/react-select'
import * as Dialog from '@radix-ui/react-dialog'
import type { ContentConfig, RoleKey } from '@/types/content'
import { Button, SuccessTickIcon } from '@/components/ui/Button'
import {
  selectContentClass,
  selectItemClass,
  selectTriggerClass,
} from '@/components/ui/SelectTrigger'
import { Logo } from './Logo'

interface StickySelectorProps {
  config: ContentConfig
  roleKey: RoleKey
  topicKey: string
  onRoleChange: (role: RoleKey) => void
  onTopicChange: (topic: string) => void
  onOpenSignup?: (source: string) => void
  visible: boolean
  /** -- When true, Setup Now button shows green success + tick (already submitted) */
  hasSubmittedDemo?: boolean
}

const ROLE_ORDER: RoleKey[] = ['service', 'sales', 'finance', 'management']

const STICKY_NAV_LINKS = [
  { label: 'Features', href: '#features', umami: 'Sticky Nav - Features' },
  { label: 'Customers', href: '#customers', umami: 'Sticky Nav - Customers' },
  { label: 'Q&A', href: '#qa', umami: 'Sticky Nav - Q&A' },
  { label: 'Pricing', href: '#pricing', umami: 'Sticky Nav - Pricing' },
] as const

function BurgerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  )
}

function truncateLabel(label: string, max = 28): string {
  if (label.length <= max) return label
  return `${label.slice(0, max - 1)}…`
}

export function StickySelector({
  config,
  roleKey,
  topicKey,
  onRoleChange,
  onTopicChange,
  onOpenSignup,
  visible,
  hasSubmittedDemo = false,
}: StickySelectorProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [dropdownRect, setDropdownRect] = useState<{ top: number; right: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const role = config.roles[roleKey]
  const topics = role?.topics ?? []
  const topicLabel = topics.find((t) => t.key === topicKey)?.label ?? topicKey
  const roleShort = role?.label ?? roleKey
  const chipSummary = truncateLabel(`${roleShort} · ${topicLabel}`, 36)

  const updateDropdownRect = () => {
    if (buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect()
      setDropdownRect({ top: r.bottom + 4, right: window.innerWidth - r.right })
    }
  }

  useEffect(() => {
    if (!menuOpen) {
      setDropdownRect(null)
      return
    }
    updateDropdownRect()
    const onScrollOrResize = () => updateDropdownRect()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  if (!visible) return null

  return (
    <div className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-line shadow-sm overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 min-w-0">
        <div className="nav:hidden shrink-0">
          <Logo variant="dark" src={config.brand.logoDark} className="h-7 w-auto" />
        </div>

        {/* -- [Mobile: About you chip opens personalisation dialog] */}
        <Dialog.Root open={aboutOpen} onOpenChange={setAboutOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="nav:hidden inline-flex items-center gap-2 min-h-11 max-w-[min(220px,52vw)] rounded-full border-2 border-brand-orange/40 bg-brand-orange/5 px-3 py-2 text-sm font-semibold text-gray-900 hover:border-brand-orange hover:bg-brand-orange/10 active:scale-[0.98] transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
              data-umami-event="Sticky - About you"
            >
              <span className="text-brand-orange shrink-0" aria-hidden>
                ✎
              </span>
              <span className="truncate">{chipSummary}</span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[60]" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-[70] w-[min(420px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-xl border border-line focus:outline-none">
              <Dialog.Title className="text-lg font-bold text-gray-900">About you</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-muted">
                We&apos;ll tailor the page to your role and goals.
              </Dialog.Description>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">I am</label>
                  <Select.Root value={roleKey} onValueChange={(v) => onRoleChange(v as RoleKey)}>
                    <Select.Trigger
                      className={selectTriggerClass('light', 'min-w-0 w-full')}
                      aria-label="Select role"
                    >
                      <Select.Value />
                      <Select.Icon />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className={selectContentClass} position="popper">
                        <Select.Viewport>
                          {ROLE_ORDER.map((r) => (
                            <Select.Item key={r} value={r} className={selectItemClass}>
                              <Select.ItemText>{config.roles[r]?.label ?? r}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    and interested in
                  </label>
                  <Select.Root value={topicKey} onValueChange={onTopicChange}>
                    <Select.Trigger
                      className={selectTriggerClass('light', 'min-w-0 w-full')}
                      aria-label="Select topic"
                    >
                      <Select.Value />
                      <Select.Icon />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className={selectContentClass} position="popper">
                        <Select.Viewport>
                          {topics.map((t) => (
                            <Select.Item key={t.key} value={t.key} className={selectItemClass}>
                              <Select.ItemText>{t.label}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Dialog.Close asChild>
                  <Button variant="primary">Done</Button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* -- [Wider sticky: inline role/topic] */}
        <span className="text-sm font-medium text-ink-muted nav:inline hidden">I am</span>
        <div className="hidden nav:contents">
          <Select.Root value={roleKey} onValueChange={(v) => onRoleChange(v as RoleKey)}>
            <Select.Trigger
              className={selectTriggerClass('light', 'min-w-[180px]')}
              aria-label="Select role"
            >
              <Select.Value />
              <Select.Icon />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className={selectContentClass} position="popper">
                <Select.Viewport>
                  {ROLE_ORDER.map((r) => (
                    <Select.Item key={r} value={r} className={selectItemClass}>
                      <Select.ItemText>{config.roles[r]?.label ?? r}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
        <span className="text-sm font-medium text-ink-muted nav:inline hidden">and interested in</span>
        <div className="hidden nav:contents">
          <Select.Root value={topicKey} onValueChange={onTopicChange}>
            <Select.Trigger
              className={selectTriggerClass('light', 'min-w-[220px]')}
              aria-label="Select topic"
            >
              <Select.Value />
              <Select.Icon />
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className={selectContentClass} position="popper">
                <Select.Viewport>
                  {topics.map((t) => (
                    <Select.Item key={t.key} value={t.key} className={selectItemClass}>
                      <Select.ItemText>{t.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {/* -- [lg+: inline section links + Setup] */}
        <div className="ml-auto hidden lg:flex items-center gap-2 shrink-0">
          {STICKY_NAV_LINKS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="inline-flex items-center min-h-12 px-3 py-2 text-sm font-medium text-gray-700 hover:text-brand-orange transition-colors"
              data-umami-event={item.umami}
            >
              {item.label}
            </a>
          ))}
          {onOpenSignup && (
            <Button
              variant={hasSubmittedDemo ? 'success' : 'primary'}
              className="px-4"
              onClick={() => onOpenSignup('Setup Now')}
              data-umami-event="Sticky Nav - Setup Now"
            >
              {hasSubmittedDemo && <SuccessTickIcon className="h-4 w-4 shrink-0" />}
              Setup Now
            </Button>
          )}
        </div>

        {/* -- [Below lg: burger for nav] */}
        <div className="ml-auto lg:ml-0 lg:hidden relative" ref={menuRef}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex items-center justify-center min-h-12 min-w-12 p-3 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <BurgerIcon />
          </button>
          {menuOpen &&
            dropdownRect &&
            createPortal(
              <div
                ref={dropdownRef}
                role="menu"
                className="fixed py-2 w-[min(280px,90vw)] rounded-lg bg-white border border-line shadow-xl z-[30] grid grid-cols-1 min-[400px]:grid-cols-2 gap-1"
                style={{
                  top: dropdownRect.top,
                  right: dropdownRect.right,
                  left: 'auto',
                }}
              >
                {STICKY_NAV_LINKS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-brand-orange/10 hover:text-brand-orange cursor-pointer col-span-1"
                    data-umami-event={item.umami}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
                {onOpenSignup && (
                  <button
                    type="button"
                    role="menuitem"
                    className={`col-span-full w-full text-left px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${
                      hasSubmittedDemo
                        ? 'text-green-700 bg-green-50 hover:bg-green-100'
                        : 'text-gray-900 hover:bg-brand-orange/10 hover:text-brand-orange'
                    }`}
                    onClick={() => {
                      onOpenSignup('Setup Now')
                      setMenuOpen(false)
                    }}
                    data-umami-event="Sticky Nav - Setup Now"
                  >
                    {hasSubmittedDemo && (
                      <SuccessTickIcon className="h-4 w-4 shrink-0 text-green-600" />
                    )}
                    Setup Now
                  </button>
                )}
              </div>,
              document.body
            )}
        </div>
      </div>
    </div>
  )
}
